import { useState, useEffect } from 'react';
import { getFilterType } from '@/Utils/getFilterType';
import { LINK_SUPPORT_DOCS_REGEX } from '@/Utils/constants';
import './style/index.scss';
import NumericInput from '@/Components/UI/Form/NumericInput/NumericInput';
import TextInput from '@/Components/UI/Form/TextInput/TextInput';
import ChipList from '@/Components/ChipList';
import { Link } from '@/Components/UI/Button/Button';

const FreeformFilter = ({
  type,
  showRegexInvalidBlock,
  defaultValue,
  onChange,
  isMultiple,
  disabled,
  setIsDisabledApplyButton,
  cypressTextFieldName = '',
  currFieldType,
  indexInFilterModel,
  setAnyFilterData,
  customProperties = {},
}) => {
  const [value, setValue] = useState([]);
  const [isValidNumber, setIsValidNumber] = useState(true);
  const [placeholder, setPlaceholder] = useState('');

  useEffect(() => {
    setValue(defaultValue !== undefined ? defaultValue : []);
  }, [defaultValue]);

  useEffect(() => {
    const newPlaceholder = determinePlaceholder();
    setPlaceholder(newPlaceholder);
  }, [customProperties, value]);

  useEffect(() => {
    validateNumber();
  }, [currFieldType, value]);

  useEffect(() => {
    if (!isValidNumber || showRegexInvalidBlock) {
      setIsDisabledApplyButton(true);
    }
  }, [isValidNumber, showRegexInvalidBlock]);

  const determinePlaceholder = () => {
    if (!value?.length) {
      if (type === 'inRange') {
        return customProperties?.inRangeType === 'from' ? 'Value From' : 'Value To';
      }
      return 'Value';
    }
    return '';
  };

  const validateNumber = () => {
    if (currFieldType?.includes('Int') && value?.length) {
      setIsValidNumber(
        Array.isArray(value)
          ? value.every((i) => Number.isInteger(Number(i)))
          : Number.isInteger(Number(value))
      );
    } else if (currFieldType?.includes('Float') && value?.length) {
      setIsValidNumber(Array.isArray(value) ? value.every((i) => !isNaN(i)) : !isNaN(value));
    } else {
      setIsValidNumber(true);
    }
  };

  const handleChange = (newValue) => {
    const inputValue =
      getFilterType(currFieldType) === 'number' ? formatNumberInput(newValue) : newValue;
    setValue(inputValue);
    onChange(inputValue);
  };

  const formatNumberInput = (input) => {
    return Array.isArray(input) ? input.map((value) => value.trim()) : input.trim();
  };

  const renderRegexInput = () => (
    <>
      <TextInput
        value={value}
        onChange={(event) => handleTextInputChange(event)}
        disabled={disabled}
        placeholder={placeholder}
        className='mx-1 h-[34px]'
        size='large'
        dataCy={cypressTextFieldName}
      />
      {type === 'regularExpression' && showRegexInvalidBlock && renderRegexInvalidBlock()}
    </>
  );

  const renderRegexInvalidBlock = () => (
    <div className='mx-1 text-xs text-ui-helper'>
      <p>Invalid expression</p>
      <Link
        href={LINK_SUPPORT_DOCS_REGEX}
        target='_blank'
        rel='noreferrer'
        className='flex items-center gap-1'
      >
        Need Help?
      </Link>
    </div>
  );

  const renderNumberInput = () => (
    <>
      <NumericInput
        value={value}
        onChange={(event) => handleTextInputChange(event)}
        disabled={disabled}
        placeholder={placeholder}
        size='large'
        dataCy={cypressTextFieldName}
      />
      {!isValidNumber && <div className='mx-1 text-xs text-ui-helper'>Must be valid number</div>}
    </>
  );

  const renderChipList = (isNumber = false) => (
    <>
      <ChipList
        value={value}
        onChange={(newValue) => {
          setValue(newValue);
          handleChange(newValue);
          setIsDisabledApplyButton(!newValue.length);
        }}
        disabled={disabled}
      />
      <span className='mx-1 text-xs text-ui-helper'>Press ENTER to add value(s)</span>
      {isNumber && !isValidNumber && (
        <div className='mx-1 text-xs text-ui-helper'>Must be valid number</div>
      )}
    </>
  );

  const handleTextInputChange = (event) => {
    const newValue = event.target.value;
    setIsDisabledApplyButton(!newValue?.length);
    setAnyFilterData({ newValue, index: indexInFilterModel });
    if (!isMultiple) handleChange(newValue);
  };

  const renderInput = () => {
    if (type === 'regularExpression' || type === 'wildcardMatch') {
      return renderRegexInput();
    }

    if (currFieldType === 'UInt64' || currFieldType === 'Int64' || currFieldType === 'Float64') {
      if (
        type === 'inRange' ||
        type === 'lessThan' ||
        type === 'greaterThan' ||
        type === 'lessThanOrEqual' ||
        type === 'greaterThanOrEqual'
      ) {
        return renderNumberInput();
      } else {
        return renderChipList(true);
      }
    }

    if (
      type === 'endsWithAny' ||
      type === 'startsWithAny' ||
      type === 'notEndsWithAny' ||
      type === 'notStartsWithAny' ||
      type === 'equalsAny' ||
      type === 'notEqualsAny' ||
      type === 'containsAny' ||
      type === 'notContainsAny'
    ) {
      return renderChipList();
    }

    return renderChipList();
  };

  return <>{renderInput()}</>;
};

export default FreeformFilter;
